import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Home, User, ShieldCheck, Handshake } from "lucide-react";
import { useAuthStore } from "stores/auth/authStore";

const Section = ({ title, icon: Icon, links }) => (
  <div className="rounded-2xl border border-[rgba(92,128,182,0.21)] bg-[rgba(92,128,182,0.12)] p-6 md:p-8 transition hover:border-[#5c80b6]/60 hover:shadow-lg">
    <div className="flex items-center gap-3 mb-4 border-b border-[#2b364b]/15 pb-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2b364b]">
        <Icon className="h-5 w-5 text-[#becce2]" />
      </span>
      <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#2b364b]">
        {title}
      </h2>
    </div>
    <ul className="space-y-3">
      {links.map(({ to, label }) => (
        <li key={to}>
          <Link
            to={to}
            className="group flex items-center gap-2 font-['Montserrat'] text-[15px] text-[#343434] transition hover:text-[#2b364b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5c80b6] rounded"
          >
            <ChevronRight className="h-4 w-4 text-[#5c80b6] transition group-hover:translate-x-1" />
            <span className="group-hover:underline">{label}</span>
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export const SiteMap = () => {
  const subdomain = useAuthStore((state) => state.domain);

  useEffect(() => {
    document.title = "Site Map | SaddleFit";
    const meta =
      document.querySelector('meta[name="description"]') ||
      (() => {
        const m = document.createElement("meta");
        m.name = "description";
        document.head.appendChild(m);
        return m;
      })();
    meta.setAttribute(
      "content",
      "Browse all pages available on SaddleFit — shop, events, account, and more."
    );
  }, []);

  const main = [
    { to: "/", label: "Home" },
    { to: "/buy", label: "Buy Saddles" },
    { to: "/event", label: "Events" },
    { to: "/custom-saddle-tree", label: "Custom Saddle Tree" },
    { to: "/partner", label: "Become a Partner" },
    { to: "/favorite", label: "Favorites" },
  ];

  const account = [
    { to: "/profile", label: "Profile" },
    { to: "/user/horses", label: "My Horses" },
    { to: "/cart", label: "Cart" },
    { to: "/my-orders", label: "My Orders" },
    { to: "/my-bookings", label: "My Bookings" },
  ];

  const legal = [
    { to: "/terms-and-conditions", label: "Terms & Conditions" },
    { to: "/privacy-policy", label: "Privacy Policy" },
  ];

  const partners = [
    { to: "/cactus", label: "Cactus" },
    { to: "/billy-cook", label: "Billy Cook" },
  ];

  return (
    <div>
      <section className="bg-[#2b364b] py-16 md:py-24">
        <div className="max-w-[1340px] mx-auto px-5 md:px-10 text-center">
          <h1 className="font-['Barlow_Condensed'] text-[48px] md:text-[72px] font-bold uppercase text-[#f7f7f6] leading-none">
            Site Map
          </h1>
          <p className="mt-4 font-['Montserrat'] text-base md:text-lg text-[#c3c9d4]">
            Browse every page on SaddleFit — shop, events, account, and more.
          </p>
        </div>
      </section>

      <div className="max-w-[1340px] mx-auto px-5 md:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          <Section title="Main" icon={Home} links={main} />
          <Section title="Account" icon={User} links={account} />
          <Section title="Legal" icon={ShieldCheck} links={legal} />
          {!subdomain && (
            <Section title="Partners" icon={Handshake} links={partners} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteMap;
